import React, { useCallback, useContext } from 'react';
import c from 'classnames';
import { useTranslation } from 'react-i18next';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';
import TextButton from '@wix/da-ds/pkg/Buttons/TextButton';
import IconButton from '@wix/da-ds/pkg/Buttons/IconButton';
import AddCircle from '@wix/da-ds/pkg/Icons/AddCircle';
import SubtractCircle from '@wix/da-ds/pkg/Icons/16x16/SubtractCircle';
import TextInput from '@wix/da-ds/pkg/formControls/dsPresets/TextInput';
import SelectField from '@wix/da-ds/pkg/Dropdown/select/SelectField';
import { SubmitFormValues } from '../../redux/types';
import { FieldFormPopupState } from '../../modals/FieldFormPopup/types';

import s from './LabelsForm.scss';

export interface Props extends FieldFormPopupState<SubmitFormValues['labels']> {
  onValidation?: (isValid: boolean) => void;
}

const LabelsForm: React.FC<Props> = ({
  currentValue: labels,
  setValue: setLabels,
  onValidation,
}) => {
  const isMobile = useContext(MobileContext);
  const { t } = useTranslation();
  const labelI18nKeys = {
    model: 'deviationSubmit.form.labels.list.type.model',
    object: 'deviationSubmit.form.labels.list.type.object',
    character: 'deviationSubmit.form.labels.list.type.character',
    aireference: 'deviationSubmit.form.labels.list.type.aireference',
    location: 'deviationSubmit.form.labels.list.type.location',
  };

  const renderedLabels =
    labels && labels.length > 0
      ? labels
      : [
          {
            type: '',
            name: '',
          },
        ];

  const validateRows = useCallback(
    values => {
      let isInvalid = false;
      isInvalid = values.some(
        label => label.type.length === 0 || label.name.length === 0
      );
      onValidation && onValidation(!isInvalid);
    },
    [onValidation]
  );

  const removeRow = useCallback(
    idx => {
      if (idx === 0) {
        return;
      }
      const newLabels = [...(labels || [])];
      newLabels.splice(idx, 1);
      setLabels(newLabels);
      validateRows(newLabels);
    },
    [labels, setLabels, validateRows]
  );

  const addRow = useCallback(() => {
    const newLabels = [
      ...(labels || []),
      {
        type: '',
        name: '',
      },
    ];
    setLabels(newLabels);
    validateRows(newLabels);
  }, [labels, setLabels, validateRows]);

  const updateRow = useCallback(
    (idx, type, name) => {
      const newLabels = [...(labels || [])];
      newLabels[idx] = { type, name };
      setLabels(newLabels);
      validateRows(newLabels);
    },
    [labels, setLabels, validateRows]
  );

  const locationLabelExists = renderedLabels
    .map(i => i.type)
    .includes('location');

  return (
    <>
      <div className={s['labels']}>
        {renderedLabels.map((label, idx) => (
          <div className={s['label']} key={`label-${idx}`}>
            <SelectField
              id={`label-type-${idx}`}
              label={t('deviationSubmit.form.labels.list.formHeading.type')}
              isLabelHidden={idx > 0}
              items={Object.entries(labelI18nKeys)
                .filter(([k, v]) =>
                  // If this is the last row and there's no location yet,
                  // Location is the only possible option
                  idx === 6 && !locationLabelExists
                    ? k === 'location'
                    : // If there's already a location in the list,
                      // prevent another from appearing
                      k !== 'location' ||
                      label.type === 'location' ||
                      !locationLabelExists
                )
                .map(([k, v]) => ({
                  value: k,
                  label: t(v),
                }))}
              isRequired
              value={label.type}
              onSelect={newType => updateRow(idx, newType, label.name)}
            />
            <TextInput
              id={`label-name-${idx}`}
              label={t('deviationSubmit.form.labels.list.formHeading.name')}
              isLabelHidden={idx > 0}
              value={label.name}
              noBottomSpacing
              onChange={e => updateRow(idx, label.type, e.target.value)}
              maxLength={255}
              maxLengthHidden
              isRequired
            />
            <IconButton
              variant="main"
              size={isMobile ? 'medium' : 'small'}
              icon={SubtractCircle}
              className={s['remove-btn']}
              iconClassName={c(
                s['remove-icon'],
                idx === 0 && s['remove-disabled']
              )}
              onClick={() => removeRow(idx)}
              aria-label={t('deviationSubmit.form.labels.list.button.remove')}
            />
          </div>
        ))}
      </div>
      {renderedLabels.length < 7 && (
        <TextButton
          variant="main"
          size="medium"
          icon={AddCircle}
          onClick={addRow}
        >
          {t('deviationSubmit.form.labels.list.button.addMore')}
        </TextButton>
      )}
    </>
  );
};
LabelsForm.displayName = 'LabelsForm';

export default LabelsForm;
